import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { ALERTS_IDS, isBerichtID } from './berichte-config.token';
import { BerichtID, AlertsUIGroup, Alert, AlertUI, RepeatOptions } from './berichte.types.ts';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { User } from 'src/app/models/api/User';
import { tapResponse } from '@ngrx/operators';

type AlertState = Record<BerichtID, AlertsUIGroup>;
type UserNameMap = Map<User['id'], User['Nachname']>;

export interface AlertsStateLoader {
  alerts: Alert[];
  users: User[];
}
const initialStateFactory = () =>
  ALERTS_IDS.reduce(
    (state, berichtID) => ((state[berichtID] = { expanded: false, count: 0, items: [] }), state),
    {} as AlertState
  );
export const AlertsStore = signalStore(
  withState(() => ({
    berichte: initialStateFactory(),
    userIdToNachname: new Map() as UserNameMap,
  })),
  withMethods(store => ({
    updateBerichtsGroup(berichtID: BerichtID) {
      const berichte = store.berichte();
      const group = berichte[berichtID];
      const updatedItems = [...group.items];
      const updatedGroup = { ...group, items: updatedItems };
      const updatedState = { ...berichte, [berichtID]: updatedGroup };
      patchState(store, { berichte: updatedState });
    },
    getUserNameJoinedString(userIDs: number[]) {
      return userIDs
        .filter(id => store.userIdToNachname().has(id))
        .map(id => store.userIdToNachname().get(id))
        .join(', ');
    },
  })),

  withMethods(store => ({
    reload: rxMethod(
      tapResponse({
        next: (reloading: boolean) => {
          if (reloading) patchState(store, { berichte: initialStateFactory() });
        },
        error: console.error,
      })
    ),
    load: rxMethod(
      tapResponse({
        next: ({ alerts, users }: AlertsStateLoader) => {
          if (!users?.length || !alerts) return;

          patchState(store, { userIdToNachname: new Map(users.map(user => [user.id, user.Nachname])) });

          for (const alert of alerts) {
            const userArr = alert.UserList?.split(', ').map(id => +id);
            const userStr: string = store.getUserNameJoinedString(userArr);

            if (!isBerichtID(alert.AlertID)) continue;
            const berichtID: BerichtID = alert.AlertID;

            const alertObj: AlertUI = {
              ID: alert.ID,
              BerichtID: berichtID,
              active: !!alert.SendActive,
              users: { idArr: userArr || [], nameStr: userStr },
              termin: alert.HTMLText,
              format: alert.ExpFormat,
            };

            const group = store.berichte()[berichtID];
            group.items.push(alertObj);

            if (alert.SendActive) {
              group.count++;
              group.expanded = true;
            }
            store.updateBerichtsGroup(berichtID);
          }
        },
        error: console.error,
      })
    ),

    updateUsersInAlert(alert: AlertUI, selectedUsers: User[]) {
      const userIDs = selectedUsers.map(user => user.id);
      alert.users = { idArr: userIDs, nameStr: store.getUserNameJoinedString(userIDs) };
      alert.isChanged = true;
      store.updateBerichtsGroup(alert.BerichtID);
    },

    updateTerminInAlert(alert: AlertUI, termin: RepeatOptions) {
      alert.termin = termin;
      alert.isChanged = true;
      store.updateBerichtsGroup(alert.BerichtID);
    },

    updateFormatInAlert(alert: AlertUI, format: 0 | 1 /* '*.xlsx' | '*.pdf' */) {
      alert.format = format;
      alert.isChanged = true;
      store.updateBerichtsGroup(alert.BerichtID);
    },

    addNewAlert(berichtID: BerichtID): AlertUI {
      const neuAlert: AlertUI = {
        ID: null,
        BerichtID: berichtID,
        termin: '0',
        active: true,
        format: 1,
        users: {
          idArr: [],
          nameStr: '',
        },
        isChanged: true,
      };
      const group = store.berichte()[berichtID];
      group.items.push(neuAlert);
      group.count++;
      store.updateBerichtsGroup(berichtID);
      return neuAlert;
    },

    deleteAlert(berichtID: BerichtID, alert: AlertUI) {
      const group = store.berichte()[berichtID];
      const index = group.items.indexOf(alert);
      group.items.splice(index, 1);
      if (alert.active) group.count--;
      store.updateBerichtsGroup(berichtID);
    },

    toggleActive(isOn: boolean, berichtID: BerichtID, alert: AlertUI) {
      const group = store.berichte()[berichtID];
      group.count += isOn ? 1 : -1;
      alert.active = isOn;
      alert.isChanged = true;
      store.updateBerichtsGroup(berichtID);
    },

    getChangedAlerts(): Alert[] {
      const updatedAlerts: Alert[] = [];
      ALERTS_IDS.forEach(berichtID => {
        const { items } = store.berichte()[berichtID];
        for (const alert of items) {
          if (alert.isChanged) {
            updatedAlerts.push({
              ID: alert.ID,
              AlertID: alert.BerichtID,
              ExpFormat: alert.format,
              HTMLText: alert.termin,
              SendActive: alert.active,
              UserList: alert.users.idArr.join(', '),
            });
          }
        }
      });
      return updatedAlerts;
    },

    resetIsChangedInAlerts() {
      ALERTS_IDS.forEach(berichtID => {
        const { items } = store.berichte()[berichtID];
        for (const alert of items) {
          delete alert.isChanged;
        }
        store.updateBerichtsGroup(berichtID);
      });
    },
    // store.updateBerichtsGroup(berichtID);
  }))
);
